import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Tag, MapPin, Search } from "lucide-react";
import { PortalNav } from "../components/PortalNav";
import { api, apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function couponLabel(c) {
  return c.type === "percentage" ? `-${c.value}%` : c.type === "fixed" ? `-€${Number(c.value).toFixed(0)}` : "Consegna gratis";
}

export default function Offers() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [loc, setLoc] = useState("");
  const [applied, setApplied] = useState("");

  const load = (l = "") => {
    setLoading(true); setErr(null);
    api.get("/portal/coupons", { params: { loc: l } })
      .then((r) => setCoupons(r.data)).catch((e) => setErr(apiErr(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <PortalNav />
      <section className="max-w-6xl mx-auto px-4 pt-12 pb-6">
        <motion.h1 {...(reduce ? {} : { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 } })}
          className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight">Offerte e <span className="text-[#FFC400]">codici sconto</span></motion.h1>
        <p className="mt-3 text-white/60 max-w-xl">Tutti i coupon attivi dei locali affiliati Pickfood. Usa il codice al checkout per risparmiare.</p>
        <div className="mt-6 max-w-xl flex gap-2">
          <div className="relative flex-1">
            <MapPin size={16} className="absolute left-3 top-3.5 text-[#FFC400]" />
            <Input value={loc} onChange={(e) => setLoc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setApplied(loc), load(loc))}
              placeholder="Filtra per CAP, via o città" className="pl-9 h-11 bg-white/[0.05] border-white/10 text-white placeholder:text-white/35 rounded-full" data-testid="offers-loc" />
          </div>
          <Button onClick={() => { setApplied(loc); load(loc); }} className="rounded-full bg-[#FFC400] hover:bg-[#E6AC00] text-[#0B0B0D] h-11 px-5 font-semibold" data-testid="offers-search"><Search size={16} className="mr-1" />Filtra</Button>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-16">
        {loading && <p className="text-white/50" data-testid="offers-loading">Caricamento offerte…</p>}
        {err && <p className="text-red-400">{err}</p>}
        {!loading && !err && coupons.length === 0 && (
          <div className="bg-white/[0.04] rounded-2xl border border-white/10 p-8 text-center" data-testid="offers-empty">
            <Tag className="mx-auto text-white/40 mb-2" size={28} />
            <p className="font-semibold">Nessuna offerta {applied ? "in questa zona" : "disponibile"}</p>
            <p className="text-sm text-white/50 mt-1">Riprova più tardi o cambia zona.</p>
          </div>
        )}
        <div className="flex flex-wrap gap-4">
          {coupons.map((c, i) => (
            <motion.div key={`${c.tenant_slug}-${c.code}-${i}`} {...(reduce ? {} : { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.4, delay: (i % 6) * 0.05 } })}
              className="w-full sm:w-[320px] rounded-2xl border border-dashed border-[#FFC400]/40 bg-[#FFC400]/[0.06] p-5" data-testid={`offer-${c.code}`}>
              <div className="flex items-center justify-between">
                <span className="font-head text-3xl font-extrabold text-[#FFC400]">{couponLabel(c)}</span>
                <span className="text-xs font-bold bg-white/10 text-white px-2.5 py-1 rounded tracking-wide">{c.code}</span>
              </div>
              <p className="font-semibold mt-3 text-white">{c.store_name}</p>
              <p className="text-xs text-white/65">{c.brand} · {c.address}</p>
              <p className="text-[11px] text-white/55 mt-1" data-testid={`offer-min-${i}`}>{c.min_order > 0 ? `Ordine minimo €${Number(c.min_order).toFixed(2)}` : "Senza ordine minimo"}{c.max_discount ? ` · max €${Number(c.max_discount).toFixed(2)}` : ""}</p>
              <Link to={`/locale/${c.tenant_slug}/${c.store_slug}`}>
                <Button className="w-full mt-4 rounded-full bg-[#FFC400] hover:bg-[#E6AC00] text-[#0B0B0D] font-semibold" data-testid={`offer-order-${c.code}`}>Ordina ora</Button>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
